import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { OtpInput } from '@/components/auth/OtpInput';
import { PhoneInput } from '@/components/auth/PhoneInput';
import { normalizeUsPhone, validateE164 } from '@/components/auth/phone';
import { useAuth } from '@/app/AuthProvider';
import { requiresMfa } from '@/lib/permissions';
import { formatPhone } from '@/lib/formatters';
import {
  describeMfaError,
  listFactorsFresh,
  useChallengePhoneFactor,
  useEnrollPhoneFactor,
  useUnenrollFactor,
  useVerifyChallenge,
} from '@/features/auth/mfa';

type Step = 'enter-phone' | 'enter-code';

interface NavState {
  /**
   * Where we should send the user once setup is complete. Set by
   * RequireMfa when it forwards a user from a financials route.
   */
  from?: { pathname: string };
}

/**
 * Where Atlas users enroll a phone for SMS-based 2-step verification.
 *
 * The page is reachable in two contexts:
 *   1. Forced — RequireMfa redirected a financials-eligible user here
 *      because they have no verified phone factor yet.
 *   2. Voluntary — the user opened it from the Topbar > Security menu
 *      because they want to add MFA before their role is upgraded.
 *
 * We don't gate this page behind RequireMfa itself (that would be a
 * loop). We do gate it behind RequireAuth so an anonymous attacker
 * can't enroll a phone on someone else's session.
 */
export function MfaSetupPage() {
  const { profile, mfa, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as NavState | null) ?? null;

  const [step, setStep] = useState<Step>('enter-phone');
  const [phone, setPhone] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const enrollMut = useEnrollPhoneFactor();
  const challengeMut = useChallengePhoneFactor();
  const verifyMut = useVerifyChallenge();
  const unenrollMut = useUnenrollFactor();

  if (loading) return <LoadingScreen />;
  if (!profile) return <Navigate to="/auth/sign-in" replace />;

  // If they're already verified there's nothing to do here.
  if (mfa.currentLevel === 'aal2') {
    const dest = navState?.from?.pathname ?? '/dashboard';
    return <Navigate to={dest} replace />;
  }

  // If they already have a verified phone factor (sat at AAL1 because
  // they haven't completed the per-session challenge yet), the right
  // surface is /auth/mfa/challenge — not this enroll page. Hitting
  // enroll here would just bump them into Supabase's max_enrolled_factors
  // ceiling for no reason.
  if (mfa.verifiedPhoneFactors.length > 0) {
    return <Navigate to="/auth/mfa/challenge" replace state={navState} />;
  }

  const isRequired = requiresMfa(profile.role);
  const phoneE164 = normalizeUsPhone(phone);
  const phoneValid = validateE164(phoneE164);
  const codeReady = code.length === 6;

  const onSendCode = async () => {
    if (!phoneValid) {
      toast.error('Enter a valid phone number, including the country code.');
      return;
    }
    try {
      // Read the *server's* current factor list, not React state — a
      // previous failed enroll can leave a draft factor we never refreshed.
      // Drop ALL non-verified phone drafts so we can't trip
      // `over_enrolled_factors` (max_enrolled_factors=2 counts unverified
      // factors too) or `mfa_factor_name_conflict`.
      const fresh = await listFactorsFresh();
      const drafts = fresh.filter(
        (f) => f.factor_type === 'phone' && f.status !== 'verified',
      );
      for (const d of drafts) {
        await unenrollMut.mutateAsync({ factorId: d.id }).catch((e) => {
          console.warn('[mfa] failed to unenroll stale draft', d.id, e);
        });
      }

      const enrolled = await enrollMut.mutateAsync({
        phoneE164,
        friendlyName: phoneE164,
      });
      const challenge = await challengeMut.mutateAsync({ factorId: enrolled.factorId });
      setFactorId(enrolled.factorId);
      setChallengeId(challenge.challengeId);
      setStep('enter-code');
      toast.success(`We sent a 6-digit code to ${formatPhone(phoneE164)}.`);
    } catch (err) {
      // Show the Supabase error code (`mfa_phone_enroll_disabled`,
      // `phone_provider_disabled`, `over_enrolled_factors`,
      // `mfa_factor_name_conflict`, etc.) so the operator can act on it
      // instead of staring at a generic message.
      toast.error(describeMfaError(err));
    }
  };

  const onResend = async () => {
    if (!factorId) return;
    try {
      const challenge = await challengeMut.mutateAsync({ factorId });
      setChallengeId(challenge.challengeId);
      setCode('');
      toast.success('Code resent.');
    } catch (err) {
      toast.error(describeMfaError(err));
    }
  };

  const onVerify = async (overrideCode?: string) => {
    if (!factorId || !challengeId) return;
    const finalCode = overrideCode ?? code;
    if (finalCode.length !== 6) return;
    try {
      await verifyMut.mutateAsync({ factorId, challengeId, code: finalCode });
      toast.success('Phone verified — 2-step authentication is on.');
      const dest = navState?.from?.pathname ?? '/dashboard';
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(describeMfaError(err));
      setCode('');
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center justify-between">
          <Logo variant="symbol" tone="humus" className="h-10 w-10" />
          {!isRequired && (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" /> Back to dashboard
            </Link>
          )}
        </div>

        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">
            <ShieldCheck className="h-3 w-3 text-old-growth" />
            {isRequired ? '2-step verification required' : 'Add 2-step verification'}
          </div>
          <h1 className="text-2xl font-medium tracking-headline leading-headline">
            {step === 'enter-phone'
              ? 'Add a phone for verification codes'
              : 'Enter the code we just sent'}
          </h1>
          {isRequired && (
            <p className="text-sm text-muted-foreground leading-body">
              Your role requires 2-step verification before you can view
              financial data.
            </p>
          )}
        </div>

        {step === 'enter-phone' ? (
          <div className="space-y-4 rounded-lg border border-border bg-card p-6">
            <div className="space-y-2">
              <Label htmlFor="phone">Mobile phone</Label>
              <PhoneInput
                id="phone"
                value={phone}
                onChange={setPhone}
                autoFocus
                disabled={enrollMut.isPending || challengeMut.isPending}
              />
            </div>
            <Button
              onClick={onSendCode}
              disabled={!phoneValid || enrollMut.isPending || challengeMut.isPending}
              className="w-full"
            >
              {enrollMut.isPending || challengeMut.isPending ? 'Sending…' : 'Send code'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 rounded-lg border border-border bg-card p-6">
            <div className="space-y-2">
              <Label>Verification code</Label>
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={(c) => onVerify(c)}
                disabled={verifyMut.isPending}
              />
              <p className="text-xs text-muted-foreground">
                Sent to <span className="font-medium text-foreground">{formatPhone(phoneE164)}</span>.
                Codes expire in 5 minutes.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setStep('enter-phone');
                  setCode('');
                }}
                disabled={verifyMut.isPending}
              >
                Use a different number
              </Button>
              <Button
                variant="outline"
                onClick={onResend}
                disabled={challengeMut.isPending || verifyMut.isPending}
              >
                {challengeMut.isPending ? 'Sending…' : 'Resend'}
              </Button>
              <Button
                onClick={() => onVerify()}
                disabled={!codeReady || verifyMut.isPending}
                className="ml-auto"
              >
                {verifyMut.isPending ? 'Verifying…' : 'Verify'}
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
